import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  CREATE_ORDER,
  CHECKOUT_RULE_TYPES,
  GET_CART,
  screenPath,
  cartLineItemsOf,
  toCartLineInputs,
  toCheckoutSummary,
  toPriceText,
  type CheckoutSummary,
  type ICreateOrder,
  type ICreateOrderVariables,
  type IGetCart,
  type IGetCartVariables,
} from '@commerce/core'
import { YALO_PALETTE, YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { PaymentMethodList, type PaymentOption } from '../components/PaymentMethodList'
import { deliveryOptionLabel } from '../checkout/deliveryOptions'
import { paymentMethodLabel, paymentMethodPresentation } from '../checkout/paymentMethods'
import { ActionBar, LabelledRow, Screen, TopBar } from '../design'
import { shippingMethods, useDemo, type Order } from '../demo'
import { useAppSelector } from '../hooks/useAppSelector'
import { useCheckoutRuleOptions } from '../hooks/useCheckoutRuleOptions'
import { SetCheckoutRuleError, useSetCheckoutRule } from '../hooks/useSetCheckoutRule'
import { usePresentationContext } from '../hooks/usePresentationContext'

/** Built once at the design's own default express fee. See `CartPage`. */
const SHIPPING_METHODS = shippingMethods()

export interface OrderDraftArgs {
  summary: CheckoutSummary
  /** The cart's lines, for the sku-keyed reorder map the summary has no skus for. */
  lines: readonly { sku: string; quantity: number }[]
  /** What the delivery screen chose, already worded. */
  delivery: string
  payment: string
  /** Injected rather than read from the clock, so a test can pin it. */
  placedAt: Date
  locale: string
}

/**
 * The order as `useDemo().placeOrder` wants it.
 *
 * `Omit<Order, 'id'>` because the id is minted by the demo state, not here.
 * The whole record is local: `docs/NOT_PORTED.md` lists the place-order
 * mutation as cut, so nothing in this object is ever sent anywhere. The lines
 * and the money are the real cart's; the order that holds them is not.
 */
export const toOrderDraft = ({
  summary,
  lines,
  delivery,
  payment,
  placedAt,
  locale,
}: OrderDraftArgs): Omit<Order, 'id'> => ({
  date: placedAt.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
  total: summary.total,
  status: 'Confirmado',
  count: summary.itemCount,
  delivery,
  payment,
  lines: summary.orderLines,
  // Last write wins for a sku bought twice — units and a case are two lines but
  // one sku, and `reorder` refills a cart keyed by sku.
  reorder: Object.fromEntries(lines.map((line) => [line.sku, line.quantity])),
})

/**
 * The payment screen.
 *
 * The cart is real — `GET_CART` over commerce-scs — and so are the payment
 * methods now: `getCheckoutRule(PAYMENT_METHODS)` asks the client's own
 * system, live, and only what it offers is drawn. `CREDIT` arrives with the
 * shopper's actual line as `amount`, which is what the credit banner and the
 * exceeded check run on. The shipping fee is still demo — it has no endpoint.
 * The totals block is where real and demo meet, which is why it says so out
 * loud whenever the number it shows is no longer one the backend agreed to.
 *
 * Placing the order is three steps in a deliberate order: persist the chosen
 * method onto the session (`setCheckoutRuleSession` — the server re-validates
 * it against a fresh offer), then empty the real cart, then record the order
 * locally. A failure at any step leaves everything before it intact and
 * nothing after it half-done.
 */
export const PaymentPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const presentation = usePresentationContext()
  const promotionConfig = useAppSelector((state) => state.config.config)
  const { deliveryDate, shippingMethodId, paymentMethodId, choosePaymentMethod, placeOrder } =
    useDemo()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [missingMethod, setMissingMethod] = useState(false)
  const [ruleError, setRuleError] = useState('')

  const { data, loading, error } = useQuery<IGetCart, IGetCartVariables>(GET_CART, {
    variables: { cartStorefrontName: slug, cartSessionUid: sessionId },
  })

  const {
    options: methodOptions,
    loading: methodsLoading,
    error: methodsError,
    refetch: refetchMethods,
  } = useCheckoutRuleOptions(CHECKOUT_RULE_TYPES.paymentMethods)
  const { setRule } = useSetCheckoutRule()

  /**
   * Creating the order is also what retires the cart: the server moves it to
   * CHECK_OUT as part of this. There is deliberately no `cartEmpty` after it —
   * that call answers "Cart is already checked out", and treating the failure
   * as "nothing was recorded" stranded the shopper on this screen with a real
   * order behind them, and a retry that could only fail because `createOrder`
   * needs an IN_PROGRESS cart.
   */
  const [createOrder, { loading: creating, error: createError }] = useMutation<
    ICreateOrder,
    ICreateOrderVariables
  >(CREATE_ORDER, { onError: () => undefined })

  const cart = data?.cart
  const lines = useMemo(
    () =>
      toCartLineInputs({
        // Grouped or flat — read the lines from whichever shape came back.
        items: cartLineItemsOf(cart),
        activePromotions: cart?.activePromotions,
        config: promotionConfig,
      }),
    [cart, promotionConfig],
  )

  const shipping = SHIPPING_METHODS.find((method) => method.id === shippingMethodId)

  // The client system's own number, not a fixture: `CREDIT` arrives with the
  // shopper's line as `amount`. No CREDIT offered means no line to check.
  const creditLine = useMemo(
    () => methodOptions.find((option) => option.data === 'CREDIT')?.amount ?? 0,
    [methodOptions],
  )

  /**
   * `cartTotal` is the backend's `cart.total` — the promotions and fees it
   * already applied are in it. `CartPage` passes the same number; if the two
   * screens disagreed the total would move between the cart and the
   * confirmation.
   */
  const summary = useMemo(
    () =>
      toCheckoutSummary(
        {
          lines,
          cartTotal: cart?.total ?? 0,
          shippingFee: shipping?.fee ?? 0,
          creditAvailable: creditLine,
        },
        presentation,
      ),
    [lines, cart, shipping, creditLine, presentation],
  )

  // The shipping fee is not the backend's, so charging it makes the total ours.
  const isEstimate = summary.shipping > 0

  /** A price, or a dash — never the empty string `toPriceText` returns. */
  const orDash = (text: string): string => (text === '' ? '—' : text)

  const creditText = toPriceText(summary.creditAvailable, presentation)

  /**
   * Why credit cannot be picked, in words the shopper can act on.
   *
   * `unavailable` is not a smaller `exceeded`. Hide-prices is a working B2B
   * feature — those customers order all the time, their pricing is settled
   * offline by their rep — so the rest of the screen keeps working and only
   * this one method drops out: nobody here, the shopper or the code, can say
   * whether the order fits a line they cannot see. The wording points at the
   * person who *can* say, rather than quoting a number this screen is not
   * allowed to show.
   */
  const creditRefusal: Record<'exceeded' | 'unavailable', string> = {
    exceeded: `No disponible · este pedido de ${summary.totalText} supera tu crédito de ${creditText}`,
    unavailable: 'No disponible · aquí no se muestran precios; tu asesor confirma el monto',
  }

  // Only what the client system offered gets drawn — a customer without
  // credit simply never sees the credit card, rather than seeing it refused.
  const options: PaymentOption[] = methodOptions.map((option) => {
    const method = paymentMethodPresentation(option.data)
    const refusal =
      method.limitedByCredit && summary.creditCheck !== 'ok'
        ? creditRefusal[summary.creditCheck]
        : null

    return {
      id: option.data,
      label: method.label,
      detail:
        option.data === 'CREDIT' && option.amount !== null
          ? `${method.detail} · línea de ${orDash(toPriceText(option.amount, presentation))}`
          : method.detail,
      // The design keeps "RECOMENDADO" on the refused card. Recommending a
      // method the shopper cannot pick is a worse read than losing the badge.
      ...(method.tag && !refusal ? { tag: method.tag } : {}),
      ...(refusal ? { disabledReason: refusal } : {}),
    }
  })

  // Pickup has no delivery date to show — the design puts the method's own
  // label in that row instead.
  const needsDate = shipping?.needsDate ?? true
  const dateMissing = needsDate && deliveryDate === null
  const deliveryText = !needsDate
    ? (shipping?.label ?? '—')
    : deliveryDate === null
      ? '—'
      : deliveryOptionLabel(deliveryDate)

  // A real link, not a handler: the way back from checkout is a known screen,
  // so it should be middle-clickable like any other.
  const deliveryPath = screenPath('delivery', { slug, sessionId })

  if (loading || methodsLoading) {
    return (
      <Screen testId="payment-page">
        <TopBar title="Método de pago" backTo={deliveryPath} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Cargando tu pedido" />
        </Box>
      </Screen>
    )
  }

  if (error) {
    return (
      <Screen testId="payment-page">
        <TopBar title="Método de pago" backTo={deliveryPath} />
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      </Screen>
    )
  }

  // Reachable by deep link or by emptying the cart in another tab. Placing an
  // order for nothing would record a real, empty, unfixable receipt.
  if (lines.length === 0) {
    return (
      <Screen testId="payment-page">
        <TopBar title="Método de pago" backTo={deliveryPath} />
        <Stack spacing={2} sx={{ p: 5, textAlign: 'center', alignItems: 'center' }}>
          <Typography fontWeight={600}>Tu carrito está vacío.</Typography>
          <Typography variant="body2" sx={{ color: YALO_SEMANTIC.fg3 }}>
            Agrega productos antes de elegir cómo pagar.
          </Typography>
          <Button component={RouterLink} to={screenPath('catalog', { slug, sessionId })} variant="contained">
            Explorar productos
          </Button>
        </Stack>
      </Screen>
    )
  }

  const canPlace = paymentMethodId !== null && !dateMissing

  const openConfirm = () => {
    if (paymentMethodId === null) {
      setMissingMethod(true)
      return
    }
    setMissingMethod(false)
    setConfirmOpen(true)
  }

  const chooseMethod = (id: string) => {
    setMissingMethod(false)
    setRuleError('')
    choosePaymentMethod(id)
  }

  const confirmOrder = async (): Promise<void> => {
    if (paymentMethodId === null) return

    // Persist the choice onto the session first — the server re-validates the
    // token against a fresh offer, and the order is what reads it back. If
    // the offer moved (credit spent intraday), nothing else has happened yet.
    try {
      // Echo the offered option back whole: the radio is keyed on `data`, but
      // the server compares against the objects it offered, so a rebuilt
      // `"CASH"` is refused even while CASH is still on offer.
      const offered = methodOptions.find((option) => option.data === paymentMethodId)
      // Gone from a list that has since refreshed — same state the server
      // would report, so take the same recovery rather than sending a guess.
      if (!offered) throw new SetCheckoutRuleError('not-offered')

      await setRule(CHECKOUT_RULE_TYPES.paymentMethods, offered)
    } catch (failure) {
      const stale = failure instanceof SetCheckoutRuleError && failure.reason === 'not-offered'
      if (stale) {
        choosePaymentMethod(null)
        await refetchMethods()
      }
      setRuleError(
        stale
          ? 'Ese método de pago ya no está disponible. Las opciones se actualizaron — elige otro.'
          : 'No pudimos guardar tu método de pago. Intenta de nuevo.',
      )
      setConfirmOpen(false)
      return
    }

    // The order first, and from the cart that still exists. `onError` swallows
    // the rejection, so a failure arrives as a missing result — and nothing has
    // happened yet: the shopper still has their cart, their choices and a
    // screen that can explain itself.
    const created = await createOrder({
      variables: { storefrontName: slug, sessionUid: sessionId },
    })
    const placed = created.data?.createOrder
    if (!placed) {
      setConfirmOpen(false)
      return
    }

    placeOrder({
      // The server's id, not a locally minted one: the order detail asks the
      // API for it, and the API only accepts a 24-character hex id.
      id: placed.id,
      reference:
        placed.externalRefNumber ||
        (placed.sequence != null ? `#${placed.sequence}` : undefined),
      ...toOrderDraft({
        summary,
        lines,
        delivery: deliveryText,
        payment: paymentMethodLabel(paymentMethodId),
        placedAt: new Date(),
        locale: presentation.locale,
      }),
    })

    setConfirmOpen(false)
    // `replace`: this screen's cart no longer exists, so Back must not return
    // to a checkout for it.
    navigate(screenPath('processing', { slug, sessionId }), { replace: true })
  }

  return (
    <Screen testId="payment-page">
      <TopBar title="Método de pago" backTo={deliveryPath} />

      <Stack spacing={2} sx={{ flex: 1, px: 2.5, py: 1, pb: 2.5 }}>
        {/* Prices hidden for this customer makes a payment screen a screen with
            no amounts on it. Saying so beats four blank rows and a total the
            shopper is asked to approve sight unseen. */}
        {summary.pricesHidden && (
          <Alert severity="info">
            Tu tienda tiene los precios ocultos. Confirma el total con tu asesor antes de finalizar
            el pedido.
          </Alert>
        )}

        {/* The credit line is money, so it goes away with the prices — and
            with a customer the client system gave no credit at all. */}
        {!summary.pricesHidden && creditLine > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              bgcolor: YALO_SEMANTIC.bgSurface,
              border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
              borderRadius: `${YALO_RADIUS.md}px`,
              p: '11px 14px',
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 7,
                height: 7,
                borderRadius: YALO_RADIUS.pill,
                bgcolor:
                  summary.creditCheck === 'exceeded'
                    ? YALO_SEMANTIC.statusDanger
                    : YALO_PALETTE.green,
                flex: 'none',
              }}
            />
            <Box>
              <Typography variant="body2" sx={{ color: YALO_SEMANTIC.fg2 }}>
                Crédito disponible{' '}
                <Box component="span" sx={{ fontWeight: 700, color: YALO_SEMANTIC.fg1 }}>
                  {orDash(creditText)}
                </Box>
              </Typography>
              <Typography variant="caption" sx={{ color: YALO_SEMANTIC.fg3 }}>
                {summary.creditCheck === 'exceeded'
                  ? `Este pedido usaría ${summary.totalText} · te faltan ${toPriceText(-summary.creditRemaining, presentation)}`
                  : `Este pedido usaría ${summary.totalText} · te quedarían ${toPriceText(summary.creditRemaining, presentation)}`}
              </Typography>
            </Box>
          </Stack>
        )}

        {methodsError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetchMethods()}>
                Reintentar
              </Button>
            }
          >
            No pudimos cargar los métodos de pago.
          </Alert>
        ) : options.length === 0 ? (
          <Alert severity="warning">
            No hay métodos de pago disponibles para tu tienda. Contacta a tu asesor.
          </Alert>
        ) : (
          <PaymentMethodList
            legend="Elige cómo pagar"
            options={options}
            value={paymentMethodId}
            onChange={chooseMethod}
          />
        )}

        <Stack
          spacing={1}
          sx={{
            bgcolor: YALO_SEMANTIC.bgSurface,
            border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
            borderRadius: `${YALO_RADIUS.lg}px`,
            p: 1.75,
          }}
        >
          <Typography variant="overline" sx={{ color: YALO_SEMANTIC.fg3 }}>
            Resumen del pedido
          </Typography>

          <LabelledRow label="Entrega" value={deliveryText} />
          <LabelledRow label="Envío" value={shipping?.label ?? '—'} />
          <Divider />
          {/* "Total del carrito", not "Subtotal": the value is the backend's
              own `cart.total`, promotions and fees included. `CartPage` names
              it the same way, and the two screens must not disagree. */}
          <LabelledRow
            label={`Total del carrito · ${summary.itemCount} ${summary.itemCount === 1 ? 'artículo' : 'artículos'}`}
            value={orDash(summary.cartTotalText)}
          />
          <LabelledRow
            label="Costo de envío"
            value={summary.shipping > 0 ? summary.shippingText : 'Gratis'}
          />
          <Divider />
          <LabelledRow
            label={isEstimate ? 'Total estimado' : 'Total'}
            value={orDash(summary.totalText)}
            strong
          />

          {/* The same seam `CartPage` names, named again here because this is
              the screen where the shopper is asked to approve the number. */}
          {isEstimate && !summary.pricesHidden && (
            <Typography variant="caption" sx={{ color: YALO_SEMANTIC.fg3 }}>
              El descuento y el envío son de esta demostración: commerce-scs no tiene endpoint para
              ninguno de los dos. La primera línea es el total que confirma el servidor.
            </Typography>
          )}
        </Stack>
      </Stack>

      <ActionBar>
        <Stack spacing={1.25}>
          {dateMissing && (
            <Alert
              severity="warning"
              action={
                <Button component={RouterLink} to={deliveryPath} size="small">
                  Elegir
                </Button>
              }
            >
              Falta la fecha de entrega.
            </Alert>
          )}

          {missingMethod && <Alert severity="error">Elige un método de pago para continuar.</Alert>}

          {ruleError !== '' && <Alert severity="error">{ruleError}</Alert>}

          {/* A failed `createOrder` leaves the cart and every choice intact —
              it runs first precisely so this is true. */}
          {createError && (
            <Alert severity="error">
              No pudimos crear tu pedido. Tu carrito sigue como estaba — intenta de nuevo.
            </Alert>
          )}

          <Button
            fullWidth
            size="large"
            variant="contained"
            // Enabled without a payment method on purpose: the CTA explains
            // what is missing when it is pressed, where a disabled button
            // explains nothing. It is disabled only for the one thing the
            // shopper cannot fix from this screen.
            disabled={dateMissing}
            onClick={openConfirm}
            sx={{
              justifyContent: 'space-between',
              bgcolor: YALO_PALETTE.green,
              color: YALO_PALETTE.neutral100,
              '&:hover': { bgcolor: YALO_PALETTE.green, filter: 'brightness(1.05)' },
            }}
          >
            <span>Finalizar pedido</span>
            {!summary.pricesHidden && <span>{summary.totalText}</span>}
          </Button>
        </Stack>
      </ActionBar>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-order-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="confirm-order-title" sx={{ textAlign: 'center' }}>
          ¿Confirmar tu pedido?
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={1}
            sx={{ bgcolor: YALO_SEMANTIC.bgSunken, borderRadius: `${YALO_RADIUS.lg}px`, p: 1.75 }}
          >
            <LabelledRow label="Total" value={orDash(summary.totalText)} strong />
            <LabelledRow label="Entrega" value={deliveryText} />
            <LabelledRow label="Pago" value={paymentMethodLabel(paymentMethodId)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={!canPlace || creating}
            onClick={() => {
              void confirmOrder()
            }}
            sx={{
              bgcolor: YALO_PALETTE.green,
              color: YALO_PALETTE.neutral100,
              '&:hover': { bgcolor: YALO_PALETTE.green, filter: 'brightness(1.05)' },
            }}
          >
            {creating ? 'Confirmando…' : 'Sí, confirmar'}
          </Button>
          <Button fullWidth onClick={() => setConfirmOpen(false)} sx={{ color: YALO_SEMANTIC.fg3 }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Screen>
  )
}
